import type { BuddyMatch } from '@wlb/shared/api/onboarding/onboardingTypes';
import profileService from '@wlb/shared/api/services/profileService';
import type { BuddyMatchItem } from '@wlb/shared/types/match';
import { resolveProfilePictureUrl } from '@wlb/shared/utils/imageUrl';

export const getOnboardingMatchUsername = (
  match: Pick<BuddyMatchItem, 'username'>,
  fallback: string,
): string => match.username?.trim() || fallback;

export const mapOnboardingMatch = (match: BuddyMatch): BuddyMatchItem => ({
  id: match.buddyId,
  name: match.name,
  username: match.userName?.trim() || null,
  userGuid: match.userGuid,
  tag: match.about,
  score: Math.max(0, Math.min(100, Math.round(match.score))),
  age: match.age,
  profilePictureUrl: resolveProfilePictureUrl(match.profilePictureUrl),
  gender: match.gender,
});

export const mapOnboardingMatches = (
  response: BuddyMatch | BuddyMatch[] | { matches?: BuddyMatch[] },
): BuddyMatchItem[] => {
  if (Array.isArray(response)) return response.map(mapOnboardingMatch);
  if ('matches' in response) return (response.matches ?? []).map(mapOnboardingMatch);
  return [mapOnboardingMatch(response as BuddyMatch)];
};

type ProfileUsernameLoader = (
  userGuid: string,
) => Promise<{ data?: { userName?: string | null } | null }>;

export const hydrateOnboardingMatchUsernames = async (
  matches: BuddyMatchItem[],
  loadProfile: ProfileUsernameLoader = profileService.getOtherUserProfile,
): Promise<BuddyMatchItem[]> =>
  Promise.all(matches.map(async match => {
    if (match.username?.trim() || !match.userGuid?.trim()) return match;

    try {
      const response = await loadProfile(match.userGuid);
      const username = response.data?.userName?.trim();
      return username ? { ...match, username } : match;
    } catch {
      return match;
    }
  }));
